import { DATE_FORMATS } from '@liza/fhir-utils';
import { Button } from '@liza/ui/components/ui/button';
import { useDateLocale } from '@liza/ui/hooks/useDateLocale';
import { cn } from '@liza/ui/utils';
import { format, parseISO } from 'date-fns';
import { noteVersionAuthorLine, type NoteVersionAuthorView } from './noteVersionAuthor';
import { SparklesIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/** Mirrors `NoteVersionSummary` from the API without importing it — see `NoteDiffView`. */
export type NoteVersionView = {
  versionId: string | undefined;
  at: string;
  author: NoteVersionAuthorView;
};

export type NoteVersionListProps = {
  /** Oldest first, as the API returns them. */
  versions: NoteVersionView[];
  /** Version ids currently being compared, highlighted in the list. */
  selectedVersionIds: string[];
  onSelectVersion: (versionId: string) => void;
};

/**
 * One version's row. Position and date, not the version id: the id is what the URL carries and what
 * cross-references against the Medplum admin UI, but it is an opaque uuid and means nothing to a
 * reader scanning a list. "3rd of 5, 12 Aug" is the orderable form, and the two serve different jobs.
 */
export const NoteVersionList = ({
  versions,
  selectedVersionIds,
  onSelectVersion,
}: NoteVersionListProps): React.ReactNode => {
  const { t } = useTranslation('records');
  const dateLocale = useDateLocale();

  return (
    <ul className="flex flex-col gap-0.5" data-testid="note-version-list">
      {versions.map((version, index) => {
        const isSelected = version.versionId !== undefined && selectedVersionIds.includes(version.versionId);
        const position = index + 1;
        const authorName = noteVersionAuthorLine(version.author, position, t);

        return (
          <li key={version.versionId ?? `${version.at}-${index}`}>
            <Button
              variant="ghost"
              size="sm"
              className={cn('h-auto w-full justify-start gap-2 px-2 py-1 text-left', isSelected && 'bg-accent')}
              // A version Medplum stored without an id cannot be addressed in the URL, so it cannot
              // be selected — the rest of the row still tells the reader it exists.
              disabled={version.versionId === undefined}
              aria-pressed={isSelected}
              onClick={() => version.versionId && onSelectVersion(version.versionId)}
              data-testid={`note-version-${index}`}
            >
              <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                {t('noteHistoryVersionLabel', { position })}
              </span>
              {version.author.kind === 'generated' && <SparklesIcon aria-hidden className="size-3 shrink-0" />}
              <span className="min-w-0 flex-1 truncate text-xs" data-phi={version.author.kind === 'person'}>
                {authorName}
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {format(parseISO(version.at), DATE_FORMATS.DATE_TIME, { locale: dateLocale.locale })}
              </span>
            </Button>
          </li>
        );
      })}
    </ul>
  );
};
